import { Box, Stack, Typography } from '@mui/material';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import { ActivityList } from '../../components/ActivityList';
import { DonutChart } from '../../components/DonutChart';
import { EmptyState } from '../../components/EmptyState';
import { contactRows } from '../../components/contactRows';
import { InfoPanel as SharedInfoPanel } from '../../components/InfoPanel';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import type { Customer } from './sample';
import type { CustomerStats } from './stats';

export const InfoPanel = ({ c }: { c: Customer }) => (
  <SharedInfoPanel
    title="Customer Information"
    rows={[
      ...contactRows(c),
      { label: 'Industry', value: c.industry },
      { label: 'Payment Terms', value: c.terms },
      { label: 'Preferred Clearance Broker', value: c.clearanceBroker || '–' },
      { label: 'Clearance Broker Customs ID', value: c.clearanceBrokerCustomsId || '–' },
      { label: 'Customer Tax ID', value: c.taxId || '–' },
      { label: 'Status', value: `${c.status} Customer` },
    ]}
  />
);

/** The most common value in a list */
const mostCommon = (values: string[]) => {
  const counts = new Map<string, number>();
  values.forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
};

/** A short summary and key points, all worked out from this customer's shipments and quotations */
export function SummaryPanel({ c, st }: { c: Customer; st: CustomerStats }) {
  const last = st.ships[st.ships.length - 1];
  const place = [c.city, c.country].filter(Boolean).join(', ');
  const insights: string[] = [];
  if (st.ships.length) {
    insights.push(`Most common route: ${mostCommon(st.ships.map((s) => `${s.from.city} → ${s.to.city}`))}`);
    insights.push(`Preferred transport mode: ${mostCommon(st.ships.map((s) => s.service))}`);
    insights.push(`Regular commodities: ${[...new Set(st.ships.map((s) => s.cargo))].join(', ')}`);
    if (last) insights.push(`Last shipment: ${last.srb} (${last.stage})`);
  }
  if (st.waitingQuote) {
    insights.push(
      `Next follow-up: Quotation for ${st.waitingQuote.srb} (${st.waitingQuote.status === 'Sent' ? 'waiting for customer response' : 'draft to send'})`,
    );
  }
  return (
    <Panel title="Summary">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'primary.main' }}>
        <AutoAwesomeOutlined fontSize="small" />
        <Typography variant="caption">Worked out from your shipments and quotations</Typography>
      </Stack>
      <Typography variant="body2">
        {c.name} is a {c.industry ? `${c.industry.toLowerCase()} ` : ''}customer{place ? ` in ${place}` : ''}.{' '}
        {st.total
          ? `They have ${st.total} shipments with us: ${st.arrived} arrived, ${st.active} active and ${st.lost} lost.`
          : 'There are no shipments with them yet.'}
      </Typography>
      {insights.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 700 }}>Key Insights</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            {insights.map((i) => (
              <Typography key={i} component="li" variant="body2">
                {i}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Panel>
  );
}

export function StatusDonutPanel({ st }: { st: CustomerStats }) {
  return (
    <Panel title="Shipment Status">
      {st.total ? (
        <DonutChart
          centerLabel="Total"
          slices={[
            { label: 'Arrived', value: st.arrived, tone: 'success' },
            { label: 'Active', value: st.active, tone: 'info' },
            { label: 'Lost', value: st.lost, tone: 'error' },
            { label: 'Other', value: 0, tone: 'neutral' },
          ]}
        />
      ) : (
        <EmptyState>No shipments yet.</EmptyState>
      )}
    </Panel>
  );
}

export const RecentActivityPanel = ({ st }: { st: CustomerStats }) => (
  <ActivityList
    items={st.events.slice(0, 5).map((e) => ({
      id: e.id,
      tone: 'info',
      date: e.date,
      text: (
        <>
          {e.subject} <StatusChip label={e.srb} tone="neutral" />
        </>
      ),
    }))}
  />
);
